// ─────────────────────────────────────────────────────────────────────────────
// Door cycle waveforms and the reference envelope (prototype 03).
//
// A single cycle on its own shows that a door node can be read. It says
// nothing about whether the cycle is unusual. The envelope answers that: the
// min/max band of the same door's cycles over the selected reporting period,
// with the displayed cycle drawn on top of it.
//
// PROVENANCE: every cycle here is MODELLED, not measured. The shape comes from
// a three-phase current profile scaled by the door's condition; the band comes
// from running that model repeatedly across the period with per-cycle noise
// and the degradation drift that data/maintenance.ts already computes. It
// demonstrates the comparison a real system makes against a door's own
// history — it is not that history. The page says so under the panel title.
//
// The amplitude drift is taken from getTrend(), so a door degrading on the
// maintenance tab degrades here at the same rate. One model, two views.
// ─────────────────────────────────────────────────────────────────────────────

import { profileFor } from './fleet';
import { doorHealth } from './doors';
import { PeriodSelection, indicesFor, WINDOW_DAYS } from './period';
import { getTrend } from './maintenance';

/** Samples per cycle, and the interval each one represents. */
export const WAVE_POINTS = 60;
export const SAMPLE_MS = 40;

/**
 * Peak current above which the node raises an advisory, in mA.
 *
 * Sized against the generator, not picked by eye. A nominal door (health 90+)
 * peaks around 1,650 mA on the end-of-stroke surge; at health 85 it reaches
 * about 1,780. A 1,500 limit therefore sat below EVERY door on the fleet and
 * the advisory fired on all sixteen, which made it meaningless. 1,850 clears
 * every door the condition badge calls Nominal and catches the ones it flags.
 */
export const PEAK_CEILING = 1850;

function rnd(n: number): number {
  return Math.abs(Math.sin(n * 9301 + 49297) * 233280) % 1;
}

/**
 * Seed for one door.
 *
 * Note the third character: without it 'A1L' and 'A1R' produce an identical
 * noise sequence, which made half the nodes on the tab show the same trace.
 */
function doorSeed(doorId: string): number {
  return doorId.charCodeAt(0) * 101
    + doorId.charCodeAt(1) * 7
    + doorId.charCodeAt(2) * 31;
}

export interface Cycle {
  current: number[];
  position: number[];
  /** Highest current in the cycle, mA. */
  peak: number;
}

/**
 * One door cycle.
 *
 * `dayIndex` is a day in the 90-day window (WINDOW_DAYS-1 is today) and
 * `cycleIndex` distinguishes cycles within that day — a door runs hundreds,
 * and their spread is what gives the envelope its width.
 */
export function generateCycle(
  unitId: string,
  doorId: string,
  dayIndex: number = WINDOW_DAYS - 1,
  cycleIndex: number = 0,
): Cycle {
  const health = Math.max(0.5, Math.min(1, doorHealth(unitId, doorId) / 100));

  // Degradation drift, relative to today, from the same trend the maintenance
  // tab plots. On a degrading door earlier cycles sit lower, so today's cycle
  // rides at the top of its own band.
  const trend = getTrend(unitId, doorId);
  const today = trend.history[trend.history.length - 1].value;
  const d = Math.max(0, Math.min(WINDOW_DAYS - 1, dayIndex));
  const drift = trend.history[d].value / today;

  const scale = (1 / health) * drift;
  const s0 = doorSeed(doorId) + profileFor(unitId).seedOffset + dayIndex * 131 + cycleIndex * 977;

  const current: number[] = [];
  const position: number[] = [];
  let peak = 0;

  for (let i = 0; i < WAVE_POINTS; i += 1) {
    const t = i / (WAVE_POINTS - 1);
    position.push(Math.round(255 / (1 + Math.exp(-10 * (t - 0.5)))));

    const noise = rnd(i * 100 + s0) * 0.3 - 0.15;
    let mA: number;
    if (i < 12) {
      // Startup surge
      mA = (800 + rnd(i + s0) * 400) * scale + noise * 100;
    } else if (i > 48) {
      // End-of-stroke surge
      mA = (1200 + rnd(i + s0 * 2) * 300) * scale + noise * 120;
    } else {
      // Mid-travel plateau
      mA = (280 + rnd(i + s0 * 3) * 100) * scale + noise * 40;
    }
    const v = Math.round(mA);
    current.push(v);
    if (v > peak) peak = v;
  }

  return { current, position, peak };
}

/** How many of today's cycles are considered when picking the one to show. */
const TODAY_CANDIDATES = 12;

/**
 * Index of today's median cycle, by peak current.
 *
 * The panel calls what it shows a 'representative cycle', so it had better be
 * one. Picking an arbitrary cycle gives a stable door a uniformly random rank
 * against its own history — about one door in ten would read as an outlier
 * purely by luck. The median reads ~50th percentile when nothing is wrong,
 * and still climbs when the door is genuinely drifting.
 */
export function representativeIndex(unitId: string, doorId: string): number {
  const peaks = Array.from({ length: TODAY_CANDIDATES }, (_, k) => ({
    k,
    peak: generateCycle(unitId, doorId, WINDOW_DAYS - 1, k).peak,
  })).sort((a, b) => a.peak - b.peak);
  return peaks[Math.floor(peaks.length / 2)].k;
}

/** Today's representative cycle — the one the panel draws. */
export function representativeCycle(unitId: string, doorId: string): Cycle {
  return generateCycle(unitId, doorId, WINDOW_DAYS - 1, representativeIndex(unitId, doorId));
}

export interface Envelope {
  /** Per-sample minimum and maximum across the reference cycles. */
  lo: number[];
  hi: number[];
  /** The cycle drawn on top of the band — the latest one. */
  cycle: Cycle;
  /** How many cycles the band was built from. */
  sampleCount: number;
  /** Highest point the band reaches, mA. */
  bandPeak: number;
  /** True when the latest cycle's peak sits above every reference cycle. */
  outside: boolean;
  /**
   * Where the latest cycle's peak falls among the reference peaks, 0–100.
   *
   * A binary in/out verdict is a knife edge: with forty reference cycles the
   * band's maximum is an extreme value, so almost any single cycle lands
   * inside it and the panel would say 'normal' even for a door that is
   * clearly drifting. The percentile always says something true — a degrading
   * door reads high in its own range long before it clears the maximum.
   */
  percentile: number;
}

/**
 * Reference band for a door over the selected period, plus the latest cycle.
 *
 * The latest cycle is excluded from the band — otherwise it would be inside it
 * by construction and the comparison would say nothing.
 */
export function cycleEnvelope(
  unitId: string,
  doorId: string,
  sel: PeriodSelection,
): Envelope {
  const days = indicesFor(sel);
  const lastDay = days[days.length - 1];
  const shownK = representativeIndex(unitId, doorId);

  // Aim for roughly three dozen reference cycles however long the period is:
  // many per day for a single day, one or two per day across a month.
  const perDay = Math.max(1, Math.min(TODAY_CANDIDATES, Math.ceil(36 / days.length)));

  const lo = new Array<number>(WAVE_POINTS).fill(Number.POSITIVE_INFINITY);
  const hi = new Array<number>(WAVE_POINTS).fill(Number.NEGATIVE_INFINITY);
  const refPeaks: number[] = [];
  let sampleCount = 0;

  days.forEach(day => {
    for (let k = 0; k < perDay; k += 1) {
      // Skip the cycle that will be drawn on top — leaving it in would put it
      // inside the band by construction.
      if (day === lastDay && k === shownK) continue;
      const c = generateCycle(unitId, doorId, day, k);
      for (let i = 0; i < WAVE_POINTS; i += 1) {
        if (c.current[i] < lo[i]) lo[i] = c.current[i];
        if (c.current[i] > hi[i]) hi[i] = c.current[i];
      }
      refPeaks.push(c.peak);
      sampleCount += 1;
    }
  });

  const cycle = generateCycle(unitId, doorId, lastDay, shownK);

  // A degenerate period would leave the band unset; fall back to the cycle
  // itself so the chart still has something finite to draw.
  if (sampleCount === 0) {
    for (let i = 0; i < WAVE_POINTS; i += 1) {
      lo[i] = cycle.current[i];
      hi[i] = cycle.current[i];
    }
  }

  const bandPeak = Math.max(...hi);
  const below = refPeaks.filter(v => v < cycle.peak).length;
  const percentile = refPeaks.length
    ? Math.round((below / refPeaks.length) * 100)
    : 0;

  return {
    lo, hi, cycle, sampleCount, bandPeak,
    outside: cycle.peak > bandPeak,
    percentile,
  };
}
